import { Checkbox, ListItemButton, ListItemText } from "@mui/material";
import useDebouncedSearchTerm from "hooks/strings/useDebouncedSearchTerm";
import useGetEquipmentPieces from "hooks/organization/useGetEquipmentPieces";
import { useState } from "react";
import { centsToDollarsInput, WorksheetLibrarySelection } from "../worksheetFormValues";
import MultiSelectFooter from "./MultiSelectFooter";
import PickerList from "./PickerList";
import PickerSearch from "./PickerSearch";
import LoadMoreButton from "./LoadMoreButton";
import { PickerProps } from "./types";

const chargeUnit = (chargeType?: string | null): string => {
  switch (chargeType) {
    case "HOURLY":
      return "HOURS";
    case "FULL_DAY":
      return "DAYS";
    case "HALF_DAY":
      return "HALF_DAY";
    default:
      return "EACH";
  }
};

export default function EquipmentPicker({ onClose, onSelect, onSelectMany }: PickerProps) {
  const { searchTerm, setSearchTerm, debouncedSearchTerm } = useDebouncedSearchTerm("");
  const { data, loading, paginatorInfo, fetchMore } = useGetEquipmentPieces({
    first: 50,
    search: debouncedSearchTerm || undefined,
  });
  const pieces = data ?? [];
  const [selected, setSelected] = useState<Record<string, WorksheetLibrarySelection>>({});

  const selectionFor = (piece: (typeof pieces)[number]): WorksheetLibrarySelection => ({
    sourceType: "equipment",
    sourceId: piece.id,
    sourceName: piece.name,
    name: piece.name,
    rate: centsToDollarsInput(piece.rate ?? 0),
    unit: chargeUnit(piece.chargeType),
    quantity: "1",
  });

  return (
    <PickerList
      title="Select Equipment"
      loading={loading}
      empty={pieces.length === 0}
      header={
        <PickerSearch value={searchTerm} placeholder="Search equipment" onChange={setSearchTerm} />
      }
      onClose={onClose}
      footer={
        <MultiSelectFooter
          selections={Object.values(selected)}
          onSelectMany={onSelectMany}
          onClose={onClose}
        />
      }
    >
      {pieces.map((piece) => {
        const selection = selectionFor(piece);

        return (
          <ListItemButton
            key={piece.id}
            selected={!!selected[piece.id]}
            onClick={() => {
              if (onSelectMany) {
                setSelected((previous) => {
                  const next = { ...previous };
                  if (next[piece.id]) {
                    delete next[piece.id];
                  } else {
                    next[piece.id] = selection;
                  }
                  return next;
                });
                return;
              }
              onSelect(selection);
              onClose();
            }}
          >
            {onSelectMany && <Checkbox checked={!!selected[piece.id]} size="small" />}
            <ListItemText primary={piece.name} secondary={chargeUnit(piece.chargeType)} />
          </ListItemButton>
        );
      })}
      {paginatorInfo?.hasMorePages && (
        <LoadMoreButton
          label="Load more equipment"
          onClick={() =>
            fetchMore({
              page: (paginatorInfo.currentPage ?? 1) + 1,
              updateQuery: (previous, { fetchMoreResult }) => {
                if (!fetchMoreResult?.getEquipmentPieces) {
                  return previous;
                }

                return {
                  ...fetchMoreResult,
                  getEquipmentPieces: {
                    ...fetchMoreResult.getEquipmentPieces,
                    data: [
                      ...(previous.getEquipmentPieces?.data ?? []),
                      ...fetchMoreResult.getEquipmentPieces.data,
                    ],
                  },
                };
              },
            })
          }
        />
      )}
    </PickerList>
  );
}
